import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from './cn';

/**
 * <Modal open={...} onClose={...} title="..." size="md">
 *   ...contenido...
 * </Modal>
 *
 * Sizes:
 *   - sm  (max-w-sm)
 *   - md  (max-w-md) — default
 *   - lg  (max-w-2xl)
 *   - xl  (max-w-4xl)
 *
 * Comportamiento:
 *   - Cierra con Escape
 *   - Cierra click overlay (si `closeOnOverlay`)
 *   - Bloquea scroll de body
 *   - Fade + scale al entrar
 *
 * Slots:
 *   - children: contenido principal
 *   - footer: nodos abajo (botones de accion)
 */
const SIZES = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
};

// Contador global de modales abiertos + snapshot del overflow original.
// Sin esto, modales anidados (ej. VariacionesModal → VariacionFormModal) capturan
// cada uno el `prev` del otro y al cerrar en orden distinto dejan el body con
// overflow: hidden bloqueado — el scroll se rompe hasta recargar la pagina.
//
// Con el contador: solo el PRIMERO en abrir guarda el original y bloquea, y solo
// el ULTIMO en cerrar restaura. Los intermedios son no-op respecto al body.
let _openModalCount = 0;
let _bodyOverflowBackup = '';
let _bodyPaddingRightBackup = '';

const Modal = ({
    open,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    closeOnOverlay = true,
    showClose = true,
    className
}) => {
    const handleKey = useCallback((e) => {
        if (e.key === 'Escape' && open) onClose?.();
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;
        document.addEventListener('keydown', handleKey);

        // Bloquear scroll del body — pero solo el PRIMER modal en abrir guarda
        // el original. Anidados incrementan el contador sin tocar el body.
        if (_openModalCount === 0) {
            _bodyOverflowBackup = document.body.style.overflow;
            _bodyPaddingRightBackup = document.body.style.paddingRight;
            // Compensar el ancho del scrollbar para que el layout no salte al bloquear
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
        }
        _openModalCount++;

        return () => {
            document.removeEventListener('keydown', handleKey);
            _openModalCount = Math.max(0, _openModalCount - 1);
            // Solo cuando cierra el ULTIMO modal se restaura el body original.
            if (_openModalCount === 0) {
                document.body.style.overflow = _bodyOverflowBackup;
                document.body.style.paddingRight = _bodyPaddingRightBackup;
            }
        };
    }, [open, handleKey]);

    if (!open) return null;

    // Render al document.body via Portal: garantiza que el overlay sea fixed
    // contra el viewport y no contra un ancestor con transform/filter/contain.
    // Sin esto, modales embebidos en paneles con scroll/transform se "encajan"
    // dentro del contenedor padre en vez de cubrir toda la pantalla.
    if (typeof document === 'undefined') return null;
    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm animate-[fade_120ms_ease-out_forwards]"
            aria-modal="true"
            role="dialog"
            onClick={(e) => {
                // El target debe ser el contenedor padre (overlay). Click sobre el dialogo
                // o sus hijos NO dispara cierre (currentTarget !== target).
                if (closeOnOverlay && e.target === e.currentTarget) onClose?.();
            }}
        >
            {/* Dialogo */}
            <div
                className={cn(
                    'relative w-full bg-surface border border-line rounded-xl shadow-soft-lg',
                    'flex flex-col max-h-[90vh] overflow-hidden',
                    'animate-[fadeScale_140ms_ease-out_forwards]',
                    SIZES[size] || SIZES.md,
                    className
                )}
            >
                {(title || showClose) && (
                    <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-line">
                        {title && <h3 className="text-base font-semibold text-text">{title}</h3>}
                        {showClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="ml-auto inline-flex items-center justify-center w-8 h-8 rounded-lg text-muted hover:text-text hover:bg-surface-2 transition"
                                aria-label="Cerrar"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-5">{children}</div>

                {footer && (
                    <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line bg-surface-2/40">
                        {footer}
                    </div>
                )}
            </div>

            <style>{`
                @keyframes fade {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes fadeScale {
                    from { opacity: 0; transform: scale(0.96); }
                    to   { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>,
        document.body
    );
};

export default Modal;
